import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private baseUrl: string = environment.BASE_URL; // Set your base URL here

  // private baseUrl: string = 'http://localhost:8283';
  constructor(private http: HttpClient) {}

  // Main method to make HTTP requests
  request<T>(
    method: string,
    route: string,
    body?: any,
    params?: any,
    headers?: HttpHeaders
  ): Observable<T> {
    const url = `${this.baseUrl}/${route}`;

    // Default headers (can be overridden)
    const httpOptions = {
      headers:
        headers || new HttpHeaders({ 'Content-Type': 'application/json' }),
      params: params
        ? new HttpParams({ fromObject: { ...params } })
        : undefined,
    };

    switch (method.toUpperCase()) {
      case 'GET':
        return this.http.get<T>(url, httpOptions);
      case 'POST':
        return this.http.post<T>(url, body, httpOptions);
      case 'PUT':
        return this.http.put<T>(url, body, httpOptions);
      case 'PATCH':
        return this.http.patch<T>(url, body, httpOptions);
      case 'DELETE':
        return this.http.delete<T>(url, httpOptions);
      case 'DELETEWITHBODY':
        return this.http.delete<T>(url, {...httpOptions,body});

      // API CALLS WITHOUT USING THE BASE URL so you must pass the full URL
      case 'GET-NON-BASEURL':
        return this.http.get<T>(route, httpOptions);
        case 'POST-NON-BASEURL':
        return this.http.post<T>(route, body, httpOptions);
        case 'PUT-NON-BASEURL':
        return this.http.put<T>(route, body, httpOptions);
        case 'PATCH-NON-BASEURL':
        return this.http.patch<T>(route, body, httpOptions);
      case 'DELETE-NON-BASEURL':
        return this.http.delete<T>(route, httpOptions);
      default:
        throw new Error('Unsupported HTTP method');
    }
  }

  // Additional helper methods for common request types
  get<T>(route: string, params?: any, headers?: HttpHeaders): Observable<T> {
    return this.request<T>('GET', route, null, params, headers);
  }

  post<T>(route: string, body: any, headers?: HttpHeaders): Observable<T> {
    return this.request<T>('POST', route, body, null, headers);
  }

  put<T>(route: string, body: any, headers?: HttpHeaders): Observable<T> {
    return this.request<T>('PUT', route, body, null, headers);
  }

  patch<T>(route: string, body: any, headers?: HttpHeaders): Observable<T> {
    return this.request<T>('PATCH', route, body, null, headers);
  }

  delete<T>(route: string, headers?: HttpHeaders): Observable<T> {
    return this.request<T>('DELETE', route, null, null, headers);
  }

  deleteWithBody<T>(route: string, body?: any, headers?: HttpHeaders): Observable<T> {
    return this.request<T>('DELETEWITHBODY', route, body, null, headers);
  }

  /**
   * Method to make a GET request and retrieve both data and headers.
   * @param endpoint The API endpoint to call.
   * @param params Optional query parameters to include in the request.
   * @param headers Optional headers to include in the request.
   * @returns An observable containing an object with both data and headers.
   */
  getDataWithHeaders<T>(
    endpoint: string,
    params?: Record<string, any>,
    headers?: HttpHeaders
  ): Observable<{ data: T; headers: HttpHeaders }> {
    const url = `${this.baseUrl}/${endpoint}`;

    // Set up HTTP options with observe: 'response' to get full response
    const httpOptions = {
      headers:
        headers || new HttpHeaders({ 'Content-Type': 'application/json' }),
      params: params ? new HttpParams({ fromObject: params }) : undefined,
      observe: 'response' as const, // Ensure observe is typed as 'response'
    };

    // Perform GET request and map the response to extract headers and body
    return this.http.get<T>(url, httpOptions).pipe(
      map((response: HttpResponse<T>) => {
        const customHeaders = {
          totalPages: response.headers.get('x-total-pages'),
        };
        return {
          data: response.body as T,
          headers: response.headers,
          response: response,
          headerKeys: response.headers.keys(),
          customHeaders,
        };
      })
    );
  }

  // ALL THE CALLS FOR ENDPOINTS THAT DO NOT USE THE BASEURL
  getNonBaseApi<T>(route: string, params?: any, headers?: HttpHeaders): Observable<T> {
    return this.request<T>('GET-NON-BASEURL', route, null, params, headers);
  }
  postNonBaseApi<T>(route: string, body: any, headers?: HttpHeaders): Observable<T> {
    return this.request<T>('POST-NON-BASEURL', route, body, null, headers);
  }

  putNonBaseApi<T>(route: string, body: any, headers?: HttpHeaders): Observable<T> {
    return this.request<T>('PUT-NON-BASEURL', route, body, null, headers);
  }

  patchNonBaseApi<T>(route: string, body: any, headers?: HttpHeaders): Observable<T> {
    return this.request<T>('PATCH-NON-BASEURL', route, body, null, headers);
  }

  deleteNonBaseApi<T>(route: string, headers?: HttpHeaders): Observable<T> {
    return this.request<T>('DELETE-NON-BASEURL', route, null, null, headers);
  }
}
